import { useEffect, useRef } from 'react';
import { useRankingStore } from '@/shared/stores/ranking.store';
import { useAuth } from '@features/auth';

interface UseStreakReminderOptions {
  /** Hour of day to show reminder (default: 20 = 8pm) */
  reminderHour?: number;
  /** Callback when streak is at risk */
  onStreakAtRisk?: (currentStreak: number) => void;
  /** Whether to show browser notification */
  showBrowserNotification?: boolean;
}

/**
 * Hook that monitors streak status and triggers reminders
 * when the user hasn't studied today and their streak is at risk.
 */
export function useStreakReminder({
  reminderHour = 20,
  onStreakAtRisk,
  showBrowserNotification = true,
}: UseStreakReminderOptions = {}) {
  const { user } = useAuth();
  const streak = useRankingStore((state) => state.streak);
  const hasShownTodayRef = useRef(false);
  const lastCheckDateRef = useRef<string | null>(null);

  useEffect(() => {
    // Only for students
    if (user?.role !== 'student' || !streak) return;

    // Reset flag if day changed
    const today = new Date().toDateString();
    if (lastCheckDateRef.current !== today) {
      hasShownTodayRef.current = false;
      lastCheckDateRef.current = today;
    }

    // Check if streak is at risk
    const { currentStreak, hasActivityToday } = streak;
    const isAtRisk = currentStreak > 0 && !hasActivityToday;

    if (!isAtRisk || hasShownTodayRef.current) return;

    // Check if it's after the reminder hour
    const now = new Date();
    const currentHour = now.getHours();

    if (currentHour >= reminderHour) {
      hasShownTodayRef.current = true;

      // Trigger callback
      onStreakAtRisk?.(currentStreak);

      // Show browser notification if enabled and permitted
      if (showBrowserNotification && 'Notification' in window) {
        if (Notification.permission === 'granted') {
          showStreakNotification(currentStreak);
        } else if (Notification.permission !== 'denied') {
          Notification.requestPermission().then((permission) => {
            if (permission === 'granted') {
              showStreakNotification(currentStreak);
            }
          });
        }
      }
    }
  }, [user?.role, streak, reminderHour, onStreakAtRisk, showBrowserNotification]);

  return {
    isAtRisk: streak ? streak.currentStreak > 0 && !streak.hasActivityToday : false,
    currentStreak: streak?.currentStreak ?? 0,
    hasActivityToday: streak?.hasActivityToday ?? false,
  };
}

function showStreakNotification(currentStreak: number) {
  try {
    new Notification('¡Tu racha está en riesgo! 🔥', {
      body: `Llevas ${currentStreak} días de racha. ¡Completa una lección rápida para mantenerla!`,
      icon: '/favicon.ico',
      tag: 'streak-reminder', // Prevents duplicate notifications
      requireInteraction: true,
    });
  } catch (error) {
    console.error('Error showing notification:', error);
  }
}
